import { Call, Device } from '@twilio/voice-sdk';

import type {
  SoftphoneCallHandlers,
  SoftphoneClient,
  SoftphoneDialParams,
  SoftphoneMediaStrategy,
} from './softphoneClient.types';

/**
 * Twilio Voice SDK errors arrive as `TwilioError` objects whose `message` is often the useless
 * generic "An unknown error has occurred. See error details for more information." The actionable
 * info lives in `code` + `description`/`explanation` (e.g. 31000 with an underlying 11200 HTTP
 * retrieval failure). Surface the code + the most specific text we have so the modal is debuggable.
 */
function errorMessage(e: unknown, fallback: string): string {
  if (e != null && typeof e === 'object') {
    const err = e as { code?: unknown; message?: unknown; description?: unknown; explanation?: unknown };
    const code = typeof err.code === 'number' ? err.code : undefined;
    const detail =
      (typeof err.description === 'string' && err.description.length > 0 && err.description) ||
      (typeof err.explanation === 'string' && err.explanation.length > 0 && err.explanation) ||
      (typeof err.message === 'string' && err.message.length > 0 && err.message) ||
      '';
    if (code != null && detail) return `${code}: ${detail}`;
    if (code != null) return `Error ${code}`;
    if (detail) return detail;
  }
  return fallback;
}

/**
 * Twilio implementation of the provider-agnostic softphone contract. Outbound calls go
 * through the TwiML App the AccessToken points at: `device.connect({ params })` sends our
 * `voiceCallId` to the server's TwiML endpoint, which resolves the real destination from
 * the voice_call row (the browser never names the number it dials).
 *
 * NOTE: the Twilio Device manages its own mic + audio playback, so the injectable
 * `SoftphoneMediaStrategy` (used by the extension's offscreen mic with Telnyx) is accepted
 * for interface parity but not yet honored here — extension + Twilio needs live validation.
 */
export class TwilioSoftphoneClient implements SoftphoneClient {
  private device: Device | null = null;
  private call: Call | null = null;
  private handlers: SoftphoneCallHandlers | null = null;
  private readonly mediaStrategy?: SoftphoneMediaStrategy;

  constructor(mediaStrategy?: SoftphoneMediaStrategy) {
    this.mediaStrategy = mediaStrategy;
  }

  on(handlers: SoftphoneCallHandlers): void {
    this.handlers = handlers;
  }

  async init(token: string): Promise<void> {
    if (this.device) {
      this.device.destroy();
      this.device = null;
    }
    const device = new Device(token, { logLevel: 'error' });
    device.on(Device.EventName.Error, (e: unknown) => {
      this.handlers?.onError(errorMessage(e, 'Voice connection error'));
    });
    this.device = device;
    // Registering isn't strictly required for outbound, but it surfaces token/connection
    // problems up front instead of on the first dial.
    await device.register();
  }

  async dial(params: SoftphoneDialParams): Promise<void> {
    const device = this.device;
    if (!device) throw new Error('Softphone is not initialized');

    this.handlers?.onStatus('connecting');
    // Only the correlation id is sent; the destination is resolved server-side from the row.
    const call = await device.connect({ params: { voiceCallId: params.voiceCallId } });
    this.call = call;

    call.on('ringing', () => this.handlers?.onStatus('ringing'));
    call.on('accept', () => this.handlers?.onStatus('active'));
    call.on('reconnected', () => this.handlers?.onStatus('active'));
    call.on('disconnect', () => this.handlers?.onStatus('ended'));
    call.on('cancel', () => this.handlers?.onStatus('ended'));
    call.on('reject', () => this.handlers?.onStatus('ended'));
    call.on('error', (e: unknown) => this.handlers?.onError(errorMessage(e, 'Call error')));
  }

  hangup(): void {
    this.call?.disconnect();
  }

  setMuted(muted: boolean): void {
    this.call?.mute(muted);
  }

  async destroy(): Promise<void> {
    try {
      this.call?.disconnect();
    } catch {
      // already gone
    }
    this.call = null;
    try {
      this.device?.destroy();
    } catch {
      // already destroyed
    }
    this.device = null;
    return Promise.resolve();
  }
}
